import {put, select, take, fork, call} from 'redux-saga/effects';
import {efServiceAssignmentTypes} from '@ace-de/eua-entity-types';
import fetchRequest from '../../application/sagas/fetchRequest';
import * as serviceCaseHelpers from '../../service-cases/sagas/serviceCaseHelpers';
import * as samaActionTypes from '../samaActionTypes';
import updateServiceAssignment from '../../service-assignments/sagas/updateServiceAssignment';
import getMapExtentFromCenter from '../../utils/getMapExtentFromCenter';

const searchSAMADoctorLocationsGoogleMaps = function* searchSAMADoctorLocationsGoogleMaps({payload}) {
    const {serviceCaseId, serviceAssignmentLineNo, searchQuery = 'Doctor'} = payload;

    const {serviceAssignments} = yield select(state => state.serviceAssignments);
    const serviceAssignmentId = `${serviceCaseId}-${serviceAssignmentLineNo}`;
    const serviceAssignment = serviceAssignments[serviceAssignmentId];

    if (!serviceAssignment || !serviceAssignment?.serviceLocation) return;

    const {serviceManager} = yield select(state => state.application);

    const googleMapsService = serviceManager.loadService('googleMapsService');

    yield* serviceCaseHelpers.setPersistencePending(serviceCaseId);

    yield put({
        type: samaActionTypes.SET_SAMA_DOCTOR_LOCATION_RECOMMENDATIONS,
        payload: {serviceAssignmentId, doctorLocationRecommendationDTOs: []},
    });

    const referentialPoint = serviceAssignment.serviceLocation;
    const mapExtent = getMapExtentFromCenter(
        referentialPoint.latitude,
        referentialPoint.longitude,
        11,
        window.innerWidth,
        window.innerHeight,
    );
    const {longestDistance} = yield call(googleMapsService.getLongestDistanceFromPoint, referentialPoint, mapExtent);
    const mapViewport = {
        southwest: {
            longitude: mapExtent.xmin,
            latitude: mapExtent.ymin,
        },
        northeast: {
            longitude: mapExtent.xmax,
            latitude: mapExtent.ymax,
        },
    };


    yield fork(
        fetchRequest,
        samaActionTypes.SEARCH_DOCTOR_LOCATION_GOOGLE_PLACES_REQUEST,
        googleMapsService.searchPlaces,
        {
            searchQuery: searchQuery,
            longitude: referentialPoint.longitude,
            latitude: referentialPoint.latitude,
            locationRadius: Math.floor(longestDistance) * 1000 || null,
            viewport: mapViewport
                ? {
                    southwest: mapViewport.southwest,
                    northeast: mapViewport.northeast,
                }
                : null,
            placeTypes: ['doctor'],
        },
    );

    const placesSearchResponseAction = yield take([
        samaActionTypes.SEARCH_DOCTOR_LOCATION_GOOGLE_PLACES_REQUEST_SUCCEEDED,
        samaActionTypes.SEARCH_DOCTOR_LOCATION_GOOGLE_PLACES_REQUEST_FAILED,
    ]);

    if (placesSearchResponseAction.error) {
        yield* serviceCaseHelpers.setPersistenceReady(serviceCaseId);
        return;
    }

    const {response: placesSearchResponse} = placesSearchResponseAction.payload;
    const {googlePlaceDTOs} = placesSearchResponse;

    yield fork(
        fetchRequest,
        samaActionTypes.FETCH_DOCTOR_LOCATION_GEOLOCATIONS_REQUEST,
        googleMapsService.sortDistances,
        {
            origin: {
                location:
                    {
                        latLng: {
                            latitude: referentialPoint.latitude,
                            longitude: referentialPoint.longitude,
                        },
                    },
            },
            destinations: googlePlaceDTOs.map(googlePlaceDTO => ({
                apiUnrelatedFields: googlePlaceDTO,
                location: {
                    latLng: {
                        latitude: googlePlaceDTO.latitude,
                        longitude: googlePlaceDTO.longitude,
                    },
                },
            })),
            primarySortingProperty: 'distanceMeters',
            secondarySortingProperty: 'duration',
        },
    );

    const fetchGeolocationsResponseAction = yield take([
        samaActionTypes.FETCH_DOCTOR_LOCATION_GEOLOCATIONS_REQUEST_SUCCEEDED,
        samaActionTypes.FETCH_DOCTOR_LOCATION_GEOLOCATIONS_REQUEST_FAILED,
    ]);

    if (fetchGeolocationsResponseAction.error) {
        yield* serviceCaseHelpers.setPersistenceReady(serviceCaseId);
        return;
    }

    const {response: fetchGeolocationResponse} = fetchGeolocationsResponseAction.payload;
    const {distances} = fetchGeolocationResponse;

    const doctorLocationRecommendationDTOs = distances.map(distance => ({
        ...distance.destination.apiUnrelatedFields,
        routeToReferentialPoint: {
            totalKilometers: distance.distanceMeters / 1000,
            totalTravelTime: Number(distance.duration.replace('s', '')) / 60,
        },
    }));

    // Determine if selected SP is within suggestion results
    let selectedLocationFromSuggestions = null;
    if (serviceAssignment.acePartner && serviceAssignment.acePartner.location) {
        const {locationName, id: locationId} = serviceAssignment.acePartner.location;

        selectedLocationFromSuggestions = doctorLocationRecommendationDTOs
            .find(doctorLocationRecommendationDTO => {
                return doctorLocationRecommendationDTO['locationName'] === locationName
                    && doctorLocationRecommendationDTO['locationId'] === locationId;
            });

        if (!selectedLocationFromSuggestions) {
            yield fork(
                fetchRequest,
                samaActionTypes.FETCH_SERVICE_LOCATION_TO_DOCTOR_LOCATION_ROUTE_REQUEST,
                googleMapsService.getRoute,
                {
                    origin: {
                        location: {
                            latLng: {
                                latitude: referentialPoint?.latitude,
                                longitude: referentialPoint?.longitude,
                            },
                        },
                    },
                    destination: {
                        location: {
                            latLng: {
                                latitude: serviceAssignment.acePartner.location.latitude,
                                longitude: serviceAssignment.acePartner.location.longitude,
                            },
                        },
                    },
                    fields: ['routes.distanceMeters', 'routes.duration', 'routes.polyline.encodedPolyline'],
                },
            );

            const routeResponseAction = yield take([
                samaActionTypes.FETCH_SERVICE_LOCATION_TO_DOCTOR_LOCATION_ROUTE_REQUEST_SUCCEEDED,
                samaActionTypes.FETCH_SERVICE_LOCATION_TO_DOCTOR_LOCATION_ROUTE_REQUEST_FAILED,
            ]);

            let routeToReferentialPoint;
            if (!routeResponseAction.error) {
                const {response} = routeResponseAction.payload;
                const {routes} = response;
                routeToReferentialPoint = routes[0];
            }

            doctorLocationRecommendationDTOs.unshift({
                ...serviceAssignment.acePartner.location.toDTO(),
                locationId,
                'routeToReferentialPoint': routeToReferentialPoint,
            });
        }

        if (selectedLocationFromSuggestions && selectedLocationFromSuggestions.locationId !== locationId) {
            yield* updateServiceAssignment({
                caller: samaActionTypes.SUBMIT_SAMA_SERVICE_LOCATION_FORM,
                assignmentType: efServiceAssignmentTypes.MEDICAL_ADVICE,
                serviceAssignmentLineNo,
                serviceCaseId,
                serviceAssignmentData: {
                    acePartner: {
                        ...serviceAssignment.acePartner,
                        id: null, // see https://computerrock.atlassian.net/browse/ACELEA-575
                        externalId: selectedLocationFromSuggestions.locationId,
                        location: {
                            ...serviceAssignment.acePartner.location,
                            locationId: selectedLocationFromSuggestions.locationId,
                        },

                    },
                },
            });
        }
    }

    yield put({
        type: samaActionTypes.SET_SAMA_DOCTOR_LOCATION_RECOMMENDATIONS,
        payload: {
            serviceAssignmentId,
            doctorLocationRecommendationDTOs,
        },
    });

    yield* serviceCaseHelpers.setPersistenceReady(serviceCaseId);
};

export default searchSAMADoctorLocationsGoogleMaps;
